/**
 * 愛媛県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 10品が農水省「うちの郷土料理」から、2品が愛媛県観光物産協会の公式ページから。
 *
 * 鯛の扱いについて。
 *  鯛は広島（Lv12 鯛めし）、香川（Lv12 鯛そうめん）、熊本（Lv10 鯛そうめん）で3回使用済み。
 *  いずれも加熱された白い身の絵になる。
 *  愛媛の鯛めしは二系統あり、東予は丸ごと炊き込み、南予は生の切り身をたれに漬けてごはんにかける。
 *  ここでは南予（宇和島）のものを採り、生の刺身と割った卵黄という、炊き込みとは別の絵にしている。
 *  Lv11 の法楽焼は、松葉と小石を敷いた浅い鍋という、他県のどの鯛料理とも違う形。
 *
 *  呼称について。公式に確認できたのは「南予（宇和島）の活き鯛めし」「中予（松山・北条）の炊き鯛めし」
 *  という書き方であって、「松山鯛めし」「北条鯛めし」という呼称はどの公式にも見当たらなかった。
 *  だから「松山鯛めし」とは書かない。名称は「宇和島の鯛めし」としている。
 *
 * 五色そうめんは入れていない。
 *  農水省の愛媛県25品にも、いよ観ネットのスポット検索にも麺類特集にも載っておらず、
 *  公式の出典がひとつも取れなかった。出典のない料理は出さない。
 *
 * 形のばらつき。
 *  薄い平たい散らばり（緋のかぶ漬）／薄い楕円の平板（じゃこ天）／渦巻き断面（タルト）／
 *  角の立った直方体（いぎす豆腐）／帯状の縞（ふくめん）／潰れた不規則片（今治焼き鳥）／
 *  竹串の細長い螺旋（太刀巻）／層のドーム（焼豚玉子飯）／浅鍋の放射状（法楽焼）／
 *  扇状の重なり（宇和島の鯛めし）。
 *  器を描く例外は伊予さつま（椀）と法楽焼（浅い鉄鍋）の2つ。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 道後温泉の「日本最古」は公式自身が「といわれる」という伝聞形。使わない。
 *  - 下灘駅の「日本一海に近い駅」は公式に記述がない。使わない。「無人駅」も未確認。
 *  - 佐田岬の「日本一細長い半島」は国・県の断定形が取れない。「細長い半島」にとどめる。
 *  - 東平の「東洋のマチュピチュ」は伝聞的。使わない。貯鉱庫跡の事実だけを書く。
 *  - 別子銅山の開坑年・閉山年は公式で確認できなかった。数字を書かない。
 *  - 夏目漱石や正岡子規の作品の引用、登場人物には触れない。
 *
 * 本番画像は public/assets/foods/ehime/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：緋のかぶ漬・いぎす豆腐・太刀巻・法楽焼（店で食べられる品に差し替え）
 * - 西条てっぱんナポリタン：始まった年は公式資料で確かめられないので書かない
 * - 西条てっぱんナポリタン：全国チェーンとのコラボ商品（西条市のプレスリリースにある）は店の宣伝になるので書かない
 * - 三津浜焼き：「広島お好み焼きの元になった」という説はネットに出るが、公式資料で確かめられないので書かない
 * - 三津浜焼き：麺を入れるようになった時期（松山市サイトは「30年ほど前」）は年が動く書き方なので書かない
 * - 松山の鍋焼きうどん：「発祥の店」「創業年」は店の名乗りなので書かない（いよ観ネットの店紹介にあるが、店の宣伝になる）
  * - 2026-10-12 地元民レビューで「ふくめん」を外し「八幡浜ちゃんぽん」を入れた（地元の人がまず挙げる品のため。旧IDは idMigrations の retiredItems）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { ehimeSceneryTour } from './ehimeScenes';
import { EHIME_FOOD_HULLS } from './ehimeFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'jakoten',
    level: 1,
    name: 'じゃこ天',
    shortName: 'じゃこ天',
    image: '/assets/foods/ehime/01_jakoten.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '骨ごと、海をすり潰す。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/jakoten_ehime.html',
    icon: { label: 'じゃこ天', shape: 'fish', baseColor: '#8a7058', accentColor: '#4a3a2a' },
    reward: {
      unlockScore: 200,
      area: '八幡浜市・宇和島市（南予地方）',
      headline: '宇和海の小魚を皮も骨もまるごとすり潰して、薄く平らに揚げた南予の練り物。',
      tip: 'エソや小アジを皮と骨ごとすり身にするため、色は白くならず、黒っぽい斑が入る。骨ごと食べるのでカルシウムが豊富。',
      links: searchLinks('宇和島市', 'じゃこ天'),
    },
  },
  {
    id: 'taruto',
    level: 2,
    name: 'タルト',
    shortName: '愛媛タルト',
    image: '/assets/foods/ehime/02_taruto.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '断面に、渦が巻く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/taruto_ehime.html',
    icon: { label: '愛媛タルト', shape: 'mochi', baseColor: '#e0b860', accentColor: '#7a3a30' },
    reward: {
      unlockScore: 500,
      area: '松山市',
      headline: '薄く焼いたカステラ生地で柚子入りのあんを巻いた、松山生まれのロール状の菓子。',
      tip: '断面が「の」の字の渦になっているのが決め手。長崎で出会った南蛮菓子を、松山の殿様が柚子と小豆あんに置きかえて作らせたのが始まりと伝えられる。',
      links: searchLinks('松山市', 'タルト 菓子'),
    },
  },
  {
    id: 'saijo-teppan-napolitan',
    level: 3,
    name: '西条てっぱんナポリタン',
    shortName: 'てっぱんナポリタン',
    image: '/assets/foods/ehime/03_saijo_teppan_napolitan.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '西条市のご当地グルメ。ナポリタンを熱々の鉄板皿で出し、具材に玉子を使う。決まりはこの2つだけで、味付けや具材は店ごとに違う。',
    source:
      'https://www.iyokannet.jp/feature/gourmet/bkyu',
    icon: { label: 'ナポリタン', shape: 'noodle-bowl', baseColor: '#d9532b', accentColor: '#f2c94c' },
    reward: {
      unlockScore: 1000,
      area: '西条市',
      headline: '鉄板皿と玉子が決まりのナポリタン',
      tip: '冷めにくい鉄板皿が定着したのは、西条にはおっとりした人が多く、ゆっくり食事を楽しむのに向いていたからと言われている。',
      links: searchLinks('西条市', '西条てっぱんナポリタン'),
      official: [{ label: '西条市観光物産協会「西条てっぱんナポリタン」', url: 'https://saijo-imadoki.jp/teppan-neapolitan/' }],
    },
  },
  {
    id: 'mitsuhama-yaki',
    level: 4,
    name: '三津浜焼き',
    shortName: '三津浜焼き',
    image: '/assets/foods/ehime/04_mitsuhama_yaki.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '三津浜周辺の店で出すお好み焼き。味付けしたそばかうどん、キャベツ、卵、三津浜で古くから作られるちくわを入れ、半月形に焼き上げる。牛脂が甘みとコクを出す。',
    source:
      'https://www.iyokannet.jp/feature/gourmet/bkyu',
    icon: { label: '三津浜焼き', shape: 'curry-plate', baseColor: '#c98a4b', accentColor: '#5b3a24' },
    reward: {
      unlockScore: 1800,
      area: '松山市三津浜',
      headline: '二つ折りの半月形のお好み焼き',
      tip: '半月形の理由には、店の小さな鉄板を有効に使うために折ったという説や、持ち帰り用に二つ折りにした名残という説がある。始まりは大正時代の一銭洋食と言われる。',
      links: searchLinks('松山市三津浜', '三津浜焼き'),
      official: [{ label: '松山市公式観光情報サイト「三津浜焼き」', url: 'https://matsuyama-sightseeing.com/topics/9-2/' }],
    },
  },
  {
    id: 'beni-madonna',
    level: 5,
    name: '紅まどんな',
    shortName: '紅まどんな',
    image: '/assets/foods/ehime/05_beni_madonna.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'ひと粒ごとに、ゼリー。',
    source: 'https://www.iyokannet.jp/feature/mikan',
    icon: { label: '紅まどんな', shape: 'berry', baseColor: '#e87828', accentColor: '#4a7a3a' },
    reward: {
      unlockScore: 3000,
      area: '松山市ほか県内全域',
      headline: '伊予柑、清見、甘平、せとか。愛媛は品種をつぎつぎ生み出してきた柑橘の県。',
      tip: '愛媛のかんきつは全国トップクラスの生産量で、品種が多いため一年を通して何かしらの柑橘が旬を迎える。紅まどんなはゼリーのような果肉が持ち味。',
      links: searchLinks('愛媛県', '紅まどんな'),
    },
  },
  {
    id: 'yawatahama-champon',
    level: 6,
    name: '八幡浜ちゃんぽん',
    shortName: '八幡浜ちゃんぽん',
    image: '/assets/foods/ehime/06_yawatahama_champon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '鶏がら・かつお・昆布などでだしをとった黄金色のスープで、あっさりした風味が特徴。太めの中華麺を使う店が多く、たっぷりの野菜と豚肉に、八幡浜特産のかまぼこやじゃこ天などの練り物が入る。',
    source:
      'https://www.mlit.go.jp/kankocho/cruise/jp/detail/125/index.html',
    icon: { label: 'ちゃんぽん', shape: 'ramen', baseColor: '#f0d070', accentColor: '#e8a0b0' },
    reward: {
      unlockScore: 4500,
      area: '八幡浜市',
      headline: '黄金色のあっさりスープ',
      tip: '練り物が具に入るのは、魚のまち八幡浜ならでは。市内には八幡浜ちゃんぽんを出す店が数多くあり、店ごとにこだわりの味が楽しめる。',
      links: searchLinks('八幡浜市', '八幡浜ちゃんぽん'),
      official: [{ label: '八幡浜市「八幡浜ちゃんぽんMAP」', url: 'https://www.city.yawatahama.ehime.jp/doc/2022111400034/' }],
    },
  },
  {
    id: 'imabari-yakitori',
    level: 7,
    name: '今治焼き鳥',
    shortName: '今治焼き鳥',
    image: '/assets/foods/ehime/07_imabari_yakitori.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '押して、焼いて、ぱりぱり。',
    source: 'https://www.iyokannet.jp/feature/gourmet/bkyu',
    icon: { label: '今治焼き鳥', shape: 'dumpling', baseColor: '#9a6030', accentColor: '#e0b070' },
    reward: {
      unlockScore: 6500,
      area: '今治市',
      headline: '串に刺さず、鉄板の上で重しをかけて押しながら焼き上げる今治独自の焼き鳥。',
      tip: '取っ手のついた鉄板で押さえ込みながら焼くため、余分な脂が落ちて皮がぱりぱりになる。串に刺さないので、一般的な焼き鳥とは形がまるで違う。',
      links: searchLinks('今治市', '焼き鳥'),
    },
  },
  {
    id: 'iyo-satsuma',
    level: 8,
    name: '伊予さつま',
    shortName: '伊予さつま',
    image: '/assets/foods/ehime/08_iyo_satsuma.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '焼いて、擂って、かける。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/satsuma_ehime.html',
    icon: { label: '伊予さつま', shape: 'rice-bowl', baseColor: '#d8c8a8', accentColor: '#8ab04a' },
    reward: {
      unlockScore: 9000,
      area: '八幡浜市（南予地方）',
      headline: '焼いた魚の身と麦味噌をすり鉢で混ぜ、だしでのばして麦ごはんにかける南予の一杯。',
      tip: 'もとは漁師が船の上で、ぱさつく麦ごはんを食べやすくするために作った料理。喉ごしがよく、食欲の落ちる夏でも食べられる。',
      links: searchLinks('八幡浜市', '伊予さつま'),
    },
  },
  {
    id: 'matsuyama-nabeyaki-udon',
    level: 9,
    name: '松山の鍋焼きうどん',
    shortName: '鍋焼きうどん',
    image: '/assets/foods/ehime/09_matsuyama_nabeyaki_udon.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'アルミの鍋に、甘めのだしと柔らかいうどん。甘く煮付けた牛肉と油揚げをのせる。甘いものが貴重だった戦後すぐに、砂糖の代わりになるようにと甘く味付けたのが始まりとされる。',
    source:
      'https://www.iyokannet.jp/feature/gourmet/noodles',
    icon: { label: '鍋焼き', shape: 'pot', baseColor: '#c0c4c8', accentColor: '#e6b45a' },
    reward: {
      unlockScore: 12000,
      area: '松山市',
      headline: 'アルミ鍋で食べる甘いうどん',
      tip: '親子3代にわたって親しまれてきた松山のソウルフード。香川県のさぬきうどんとは味も食感もまったく違い、季節を問わずアルミ鍋で熱々を食べる。',
      links: searchLinks('松山市', '鍋焼きうどん'),
      official: [{ label: '松山市公式観光情報サイト「鍋焼きうどん」', url: 'https://matsuyama-sightseeing.com/spot/46-2/' }],
    },
  },
  {
    id: 'yakibuta-tamagomeshi',
    level: 10,
    name: '焼豚玉子飯',
    shortName: '焼豚玉子飯',
    image: '/assets/foods/ehime/10_yakibuta_tamagomeshi.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '三段重ねを、くずす。',
    source: 'https://www.iyokannet.jp/feature/gourmet/bkyu',
    icon: { label: '焼豚玉子飯', shape: 'curry-plate', baseColor: '#8a3020', accentColor: '#e8a828' },
    reward: {
      unlockScore: 16000,
      area: '今治市',
      headline: '下からごはん、焼豚、半熟の目玉焼き。甘辛いたれと胡椒がかかる今治の丼。',
      tip: '中華料理店の賄いから広まった一杯で、層の順番が決まっている。半熟の黄身をくずして、焼豚と甘辛いたれごとごはんに混ぜて食べる。',
      links: searchLinks('今治市', '焼豚玉子飯'),
    },
  },
  {
    id: 'uwajima-taimeshi',
    level: 11,
    name: '宇和島の鯛めし',
    shortName: '宇和島鯛めし',
    image: '/assets/foods/ehime/11_uwajima_taimeshi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '炊かない。生のまま、のせる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/taimeshi_ehime.html',
    icon: { label: '宇和島鯛めし', shape: 'sashimi-platter', baseColor: '#e8b0b0', accentColor: '#e8a828' },
    reward: {
      unlockScore: 22000,
      area: '宇和島市・西予市（南予地方）',
      headline: '生の鯛の切り身を、醤油とみりんと卵を合わせたたれに漬け、たれごと熱いごはんへ。',
      tip: '愛媛の鯛めしは二系統ある。今治など東予は鯛を丸ごと米と炊き込み、宇和島など南予は生の切り身をたれに漬けてごはんにかける。南予のものは漁師の船上の料理から生まれた。',
      links: searchLinks('宇和島市', '鯛めし'),
    },
  },
  {
    id: 'ehime-tai-somen',
    level: 12,
    name: '鯛そうめん',
    shortName: '鯛そうめん',
    image: '/assets/foods/ehime/12_ehime_tai_somen.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: 'タイを一尾まるごと姿煮にし、ゆでて波のように盛ったそうめんと大皿に盛る。干ししいたけ、錦糸卵、ねぎを散らし、タイの煮汁をつけ汁やかけ汁にする。松山地域では五色素麺を使うことが多い。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/tai_soumen_ehime.html',
    icon: { label: '鯛そうめん', shape: 'fish', baseColor: '#d9534f', accentColor: '#f3e9c6' },
    reward: {
      unlockScore: 30000,
      area: '愛媛県内全域（五色素麺は松山地域）',
      headline: '鯛を丸ごと一尾のせた祝いの大皿',
      tip: '喜寿の祝いやお食い初め、結婚披露宴など、家族や親族の祝いの席で振る舞われる。特に瀬戸内海沿岸でよく食べられる。',
      links: searchLinks('愛媛県', '鯛そうめん'),
      official: [{ label: 'いよ観ネット「まだまだあるよ！鯛グルメ」', url: 'https://www.iyokannet.jp/feature/tai/gourmet' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const ehimeFoodStage: StageDefinition = {
  regionId: 'ehime',
  regionName: '愛媛県',
  mode: 'food',
  title: '愛媛めしマージ',
  theme: {
    primaryColor: '#c86a20',
    secondaryColor: '#e8b840',
    accentColor: '#f4d478',
    backgroundColor: '#1a1410',
  },
  /** 背景ツアー「愛媛めぐり」。軸は一日の時間。夜明けの石鎚山から、夜の道後温泉本館へ。 */
  scenery: {
    tour: ehimeSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, EHIME_FOOD_HULLS),
};
